import React, { useEffect, useRef } from 'react';
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        observer.disconnect();
      }
    }, {
      threshold
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}
export function PainSection() {
  const ref = useScrollReveal();
  return <section className="w-full py-20 sm:py-28" style={{
    backgroundColor: '#0a0a0a'
  }} aria-label="The pain every man knows" data-id="element-333">
      <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-334">
        <div ref={ref} data-id="element-335">
          {/* Section label */}
          <div className="flex items-center gap-3 mb-8" data-id="element-336">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-337" />
            <span className="section-label" data-id="element-338">
              The Problem Nobody Talks About
            </span>
          </div>

          {/* Opening */}
          <p style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontStyle: 'italic',
          color: '#F5F5F5',
          lineHeight: 1.5,
          marginBottom: '2rem'
        }} data-id="element-339">
            You know the feeling.
          </p>

          {/* Story paragraphs */}
          <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.05rem',
          color: '#cccccc',
          lineHeight: 1.85
        }} className="space-y-5" data-id="element-340">
            <p data-id="element-341">
              You sit down in the chair. New shop. New barber. And before the
              cape even hits your shoulders, you're already bracing yourself —
              because you know what's coming.
            </p>
            <p data-id="element-342">
              The questions you've answered a hundred times.{' '}
              <span style={{
              color: '#F5F5F5',
              fontStyle: 'italic'
            }} data-id="element-343">
                "How short on the sides?"
              </span>{' '}
              The nervous hesitation when you say{' '}
              <span style={{
              color: '#F5F5F5',
              fontStyle: 'italic'
            }} data-id="element-344">
                "mid fade with a taper."
              </span>{' '}
              The moment they turn you to the mirror and it's... fine. Not what
              you pictured. Not what you asked for. Just fine.
            </p>
            <p data-id="element-345">
              So you pay. You tip. You leave telling yourself you need to find
              someone better.
            </p>
          </div>

          {/* Divider */}
          <div className="my-10 gold-divider-full" data-id="element-346" />

          {/* The real problem */}
          <div className="p-6 sm:p-8 rounded-sm" style={{
          backgroundColor: '#111111',
          borderLeft: '3px solid #C9A84C'
        }} data-id="element-347">
            <p style={{
            fontFamily: 'var(--font-callout)',
            fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
            fontWeight: 600,
            color: '#F5F5F5',
            lineHeight: 1.5,
            letterSpacing: '0.02em'
          }} data-id="element-348">
              But here's the problem nobody talks about:
            </p>
            <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.05rem',
            color: '#cccccc',
            lineHeight: 1.8,
            marginTop: '1rem'
          }} data-id="element-349">
              Finding a great barber isn't hard because great barbers don't
              exist.
            </p>
            <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.05rem',
            color: '#cccccc',
            lineHeight: 1.8,
            marginTop: '0.75rem'
          }} data-id="element-350">
              It's hard because most shops make you choose between skill and
              convenience. Either you wait three days for the guy who actually
              knows what he's doing — or you walk in anywhere, roll the dice,
              and hope for the best.
            </p>
          </div>

          {/* Pain points */}
          <div className="mt-12" data-id="element-351">
            <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
            fontWeight: 700,
            color: '#F5F5F5',
            marginBottom: '1.5rem'
          }} data-id="element-352">
              Here in Hialeah,{' '}
              <span style={{
              color: '#C9A84C'
            }} data-id="element-353">
                your look matters.
              </span>
            </h2>

            <div style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.05rem',
            color: '#cccccc',
            lineHeight: 1.85
          }} className="space-y-5" data-id="element-354">
              <p data-id="element-355">
                You're not getting dressed up for nothing. You've got a
                reputation. A presence. A standard you hold yourself to.
              </p>
              <p data-id="element-356">
                Your cut is the first thing people notice. It tells them whether
                you're sharp or sloppy. Successful or struggling. Put-together
                or just getting by.
              </p>
              <p data-id="element-357">
                And when your cut is off — even slightly — you feel it. You feel
                it when you catch yourself in a reflection and something's not
                right. You feel it in the way you carry yourself.
              </p>
            </div>

            {/* Pain cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10" data-id="element-358">
              <PainCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-360">
                    <circle cx="12" cy="12" r="10" data-id="element-361" />
                    <line x1="15" y1="9" x2="9" y2="15" data-id="element-362" />
                    <line x1="9" y1="9" x2="15" y2="15" data-id="element-363" />
                  </svg>} text="Barber doesn't know your name or remember your style" data-id="element-359" />
              <PainCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-365">
                    <circle cx="12" cy="12" r="10" data-id="element-366" />
                    <line x1="15" y1="9" x2="9" y2="15" data-id="element-367" />
                    <line x1="9" y1="9" x2="15" y2="15" data-id="element-368" />
                  </svg>} text="You leave second-guessing yourself every single time" data-id="element-364" />
              <PainCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-370">
                    <circle cx="12" cy="12" r="10" data-id="element-371" />
                    <line x1="15" y1="9" x2="9" y2="15" data-id="element-372" />
                    <line x1="9" y1="9" x2="15" y2="15" data-id="element-373" />
                  </svg>} text="Treated like just another head to get through before closing" data-id="element-369" />
            </div>

            <p className="mt-8" style={{
            fontFamily: 'var(--font-callout)',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: '#F5F5F5',
            letterSpacing: '0.03em'
          }} data-id="element-374">
              That's not respect. That's a production line.{' '}
              <span style={{
              color: '#C9A84C'
            }} data-id="element-375">
                You deserve better.
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function PainCard({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="p-5 rounded-sm flex flex-col gap-3" style={{
    backgroundColor: '#111111',
    border: '1px solid rgba(139,26,26,0.3)'
  }} data-id="element-376">
      {icon}
      <p style={{
      fontFamily: 'var(--font-body)',
      fontSize: '0.9rem',
      color: '#aaa',
      lineHeight: 1.6
    }} data-id="element-377">
        {text}
      </p>
    </div>;
}